<script setup lang="ts">
import { ref } from 'vue';
import { Instagram, Play, Pause } from 'lucide-vue-next';
import { useResponsive } from '@app/composables/responsive';

const videoReels = ref([
  {
    id: 1,
    author: 'רעות, ספורטתרפיסטית',
    img: 'https://ik.imagekit.io/torimapp/website/image_RUS1Ehdqs.png',
    video: 'https://ik.imagekit.io/torimapp/website/reut_testimonial_mC760Ltai.mp4',
    instagram: 'reut_sportherapy',
    isPlaying: true,
    isMuted: true,
  },
  {
    id: 2,
    author: 'בל, בעלת קליניקה',
    img: 'https://ik.imagekit.io/torimapp/website/image_P3ziSovSp.png',
    video: 'https://ik.imagekit.io/torimapp/website/bell_testimonial_DO_2OLulh.mp4',
    instagram: 'belle.waisberg_microbladind',
    isPlaying: true,
    isMuted: true,
  }
]);

const { isMobile } = useResponsive();

const togglePlay = (reel: any, event: Event) => {
  const videoElement = (event.currentTarget as HTMLElement).querySelector('video');
  if (videoElement) {
    if (reel.isPlaying) {
      videoElement.pause();
    } else {
      videoElement.play();
      reel.isMuted = false;
      videoElement.muted = false;
    }
    reel.isPlaying = !reel.isPlaying;
  }
};
</script>

<template>
  <section class="pb-8 pt-12 lg:py-24 bg-white relative overflow-hidden border-b border-slate-100">
    <div class="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_bottom_left,_var(--tw-gradient-stops))] from-pink-50/80 via-white to-white pointer-events-none"></div>

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
      <div class="text-center mb-12 sm:mb-16">
        <h2 class="text-3xl md:text-5xl font-black text-slate-900 mb-4 tracking-tight">
          הם כבר התקדמו. <span class="text-transparent bg-clip-text bg-gradient-to-r from-pink-500 to-purple-600">ומה איתכם?</span>
        </h2>
        <p class="text-slate-600 text-sm sm:text-lg">
          בעלי ובעלות עסקים מכל הארץ משתפים איך שפרנו להם את העסק.
        </p>
      </div>

      <!-- Horizontal Scroll on Mobile, Grid on Desktop -->
      <div class="flex overflow-x-auto lg:grid lg:grid-cols-4 gap-4 sm:gap-6 pb-8 pt-4 snap-x snap-mandatory hide-scrollbar items-center -mx-4 px-4 sm:-mx-6 sm:px-6 lg:mx-0 lg:px-0">
        <div v-if="!isMobile"></div>
        <div v-for="reel in videoReels" :key="reel.id" @click="togglePlay(reel, $event)" class="relative w-[240px] sm:w-[280px] lg:w-full shrink-0 aspect-[9/16] bg-slate-100 rounded-[2rem] overflow-hidden group snap-center shadow-lg shadow-slate-200/50 border border-slate-200 hover:border-pink-300 transition-all cursor-pointer">
          <!-- Video Element -->
          <video
            :src="reel.video"
            :autoplay="!isMobile"
            loop
            muted
            playsinline
            class="w-full h-full object-cover transition-all duration-700"
            :class="[reel.isPlaying ? 'opacity-100' : 'opacity-80']"
          ></video>

          <!-- Play/Pause Button Overlay -->
          <div class="absolute inset-0 flex items-center justify-center transition-opacity duration-300" :class="[reel.isPlaying ? 'opacity-0 group-hover:opacity-100' : 'opacity-100']">
            <div class="w-14 h-14 bg-white/40 backdrop-blur-md rounded-full flex items-center justify-center group-hover:scale-110 transition-all duration-300 shadow-md hover:bg-pink-500">
              <Pause v-if="reel.isPlaying" class="w-6 h-6 text-white fill-white" />
              <Play v-else class="w-6 h-6 text-white fill-white ml-1" />
            </div>
          </div>

          <!-- Author & Info -->
          <div class="absolute bottom-0 left-0 w-full p-5 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent transition-opacity duration-300">
            <p class="text-white font-bold text-sm sm:text-base drop-shadow-md">{{ reel.author }}</p>
            <a class="flex items-center gap-1.5 mt-1.5 text-slate-200 text-xs font-medium hover:text-white transition-colors" :href="`https://www.instagram.com/${reel.instagram}`" target="_blank" @click.stop>
              <Instagram class="w-4 h-4 text-white shrink-0" />
              <span class="text-white">{{reel.instagram}}</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.hide-scrollbar::-webkit-scrollbar { display: none; }
.hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
</style>
